import { Badge, Card, CardHeader, EmptyState, SegmentedControl, SegmentedTabs, Table, formatDate, formatTime, type Column } from "@helmr/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listAtRisk, listCheckins, type AtRiskMember, type Checkin } from "../../api/checkins";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { PersonCell, Problem, RowsSkeleton, useArea } from "../members/common";
import { reasonText, t } from "../members/strings";
import s from "../members/members.module.css";

const A = t.attendance;
type Tab = "checkins" | "atrisk";
type Decision = "all" | "allow" | "deny";

export function AttendancePage() {
  const { ctx } = useArea();
  const [params, setParams] = useSearchParams();
  // Each tab is its own lever (D57): the check-in log is `members.checkins`, at-risk is `members.at_risk`.
  const shown = { checkins: ctx.has("members.checkins"), atrisk: ctx.has("members.at_risk") };
  const asked: Tab = params.get("tab") === "atrisk" ? "atrisk" : "checkins";
  const tab: Tab = shown[asked] || ctx.status === "loading" ? asked : asked === "checkins" ? "atrisk" : "checkins";
  if (ctx.status !== "loading" && !areaAllows("attendance", ctx.has)) return <NoAccess />;
  const options = [{ value: "checkins" as const, label: A.tabs.checkins }, { value: "atrisk" as const, label: A.tabs.atRisk }].filter((o) => ctx.status === "loading" || shown[o.value]);
  const setTab = (next: Tab) => setParams(next === "checkins" ? {} : { tab: next }, { replace: true });
  return (
    <div className={s.page}>
      <div className={s.head}>
        <div>
          <h1 tabIndex={-1}>{A.title}</h1>
          <p>{A.intro}</p>
        </div>
      </div>
      {options.length > 1 ? <SegmentedTabs label="Attendance sections" value={tab} onChange={setTab} options={options} /> : null}
      {tab === "checkins" ? <CheckinLog /> : <AtRisk />}
    </div>
  );
}

function CheckinLog() {
  const { client, ctx, today, scope, branchId } = useArea();
  const [date, setDate] = useState(today);
  const [decision, setDecision] = useState<Decision>("all");
  const q = useInfiniteQuery({
    queryKey: ["checkins", ...scope, date, decision],
    queryFn: ({ pageParam }) => listCheckins(client, { date, branchId, decision: decision === "all" ? undefined : decision, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (l) => l.nextCursor,
    enabled: !!date && ctx.status !== "loading",
    retry: false,
  });
  const rows = q.data?.pages.flatMap((p) => p.checkins) ?? [];
  const columns: Column<Checkin>[] = [
    { key: "time", header: A.columns.time, cell: (c) => <span className={s.mono}>{formatTime(c.scannedAt, ctx.timeZone)}</span> },
    { key: "member", header: A.columns.member, cell: (c) => <PersonCell id={c.memberId} name={c.memberName} to={`/members/${c.memberId}`} /> },
    { key: "result", header: A.columns.result, cell: (c) => <Badge tone={c.decision === "allow" ? "ok" : "crit"} dot>{c.decision === "allow" ? A.allow : A.deny}</Badge> },
    { key: "why", header: A.columns.why, cell: (c) => <span className={s.trunc}>{c.reasons.length ? c.reasons.map(reasonText).join(", ") : "—"}</span> },
    { key: "counted", header: A.columns.counted, cell: (c) => (c.countedAsVisit ? A.counted : <span className={s.muted}>{A.notCounted}</span>) },
  ];
  return (
    <Card>
      <CardHeader title={A.tabs.checkins} subtitle={formatDate(date || today, ctx.locale, "full")} />
      <div className={s.bar}>
        <label className={s.tabsRow}>
          <span className={s.legend}>{A.date}</span>
          <input type="date" className={s.input} style={{ width: "auto" }} value={date} max={today} onChange={(e) => setDate(e.target.value)} />
        </label>
        <SegmentedControl label={A.decision} value={decision} onChange={setDecision} options={[{ value: "all", label: A.all }, { value: "allow", label: A.allowed }, { value: "deny", label: A.denied }]} />
      </div>
      {!date ? <EmptyState icon="finger" title={A.pickDay} />
        : q.isPending ? <RowsSkeleton label="Loading check-ins" />
        : q.error ? <Problem error={q.error} title={A.checkinsError} onRetry={() => void q.refetch()} />
        : rows.length === 0 ? <EmptyState icon="finger" title={A.emptyCheckins} description={A.emptyCheckinsBody} />
        : <Table label="Check-ins" columns={columns} rows={rows} rowKey={(c) => c.id} minWidth={640} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={A.summary(rows.length, q.hasNextPage)} />}
    </Card>
  );
}

function AtRisk() {
  const { client, ctx, scope } = useArea();
  const [level, setLevel] = useState<"watch" | "at_risk">("watch");
  const q = useInfiniteQuery({
    queryKey: ["at-risk", ...scope, level],
    queryFn: ({ pageParam }) => listAtRisk(client, { level, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (l) => l.nextCursor,
    enabled: ctx.status !== "loading",
    retry: false,
  });
  const rows = q.data?.pages.flatMap((p) => p.members) ?? [];
  const columns: Column<AtRiskMember>[] = [
    { key: "member", header: A.riskColumns.member, cell: (m) => <PersonCell id={m.memberId} name={m.name} to={`/members/${m.memberId}`} /> },
    { key: "level", header: A.riskColumns.level, cell: (m) => <Badge tone={m.level === "at_risk" ? "crit" : "warn"} dot>{m.level === "at_risk" ? A.atRisk : A.watch}</Badge> },
    { key: "why", header: A.riskColumns.why, cell: (m) => <span className={s.reasons}>{m.reasons.map((r) => <span key={r.code} title={r.detail}>{r.detail}</span>)}</span> },
  ];
  return (
    <Card>
      <CardHeader title={A.tabs.atRisk} />
      <div className={s.bar}>
        <SegmentedControl label={A.level} value={level} onChange={setLevel} options={[{ value: "watch", label: A.levels.watch }, { value: "at_risk", label: A.levels.at_risk }]} />
      </div>
      {q.isPending ? <RowsSkeleton label="Loading at-risk members" />
        : q.error ? <Problem error={q.error} title={A.riskError} onRetry={() => void q.refetch()} />
        // The API pages members, then filters: an empty page can have more behind it.
        : rows.length === 0 && !q.hasNextPage ? <EmptyState icon="users" title={A.emptyRisk} description={A.emptyRiskBody} />
        : <Table label="At-risk members" columns={columns} rows={rows} rowKey={(m) => m.memberId} minWidth={560} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={A.summary(rows.length, q.hasNextPage)} />}
    </Card>
  );
}
